import Box from '@mui/material/Box';
import uniqueId from 'lodash/uniqueId';
// This MultiTouch lib is used for 2-finger panning.
// which prevents user to experience map-scroll trap, while scrolling the page.
// https://github.com/mapbox/mapbox-gl-js/issues/2618
// TODO: we should add an overlay with text "use two fingers to pan".
import { memo, useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import { useStore } from 'react-redux';

import config from '../../config';
import { circlePolyline } from '../../util/maps';
import { mapboxLngLatToSDKLatLng } from '../SearchMap/SearchMapWithMapbox.util';
import PoiLayerControl from './PoiLayerControl';

const mapMarker = (config) => {
  const { enabled, url, width, height, onDragEnd } = config;
  if (enabled) {
    const element = document.createElement('div');
    element.style.backgroundImage = `url(${url})`;
    element.style.width = `${width}px`;
    element.style.height = `${height}px`;
    const marker = new window.mapboxgl.Marker({ element, draggable: true });
    marker.on('dragend', () => onDragEnd(marker.getLngLat()));
    return marker;
  } else {
    return new window.mapboxgl.Marker();
  }
};

const circleLayer = (center, mapsConfig, layerId) => {
  const path = circlePolyline(center, mapsConfig.fuzzy.offset).map(([lat, lng]) => [lng, lat]);
  return {
    id: layerId,
    type: 'fill',
    source: {
      type: 'geojson',
      data: {
        type: 'Feature',
        geometry: {
          type: 'Polygon',
          coordinates: [path],
        },
      },
    },
    paint: {
      'fill-color': mapsConfig.fuzzy.circleColor,
      'fill-opacity': 0.2,
    },
  };
};

const generateFuzzyLayerId = () => {
  return uniqueId('fuzzy_layer_');
};

/**
 * @typedef {Object} DynamicMapboxMapProps
 * @property {string} [address]
 * @property {{ lat: number, lng: number } | null} [center]
 * @property {number} [zoom]
 * @property {Object} [mapsConfig]
 * @property {boolean} [mapPicker]
 * @property {function} [handleChangeLocation]
 * @property {string} [containerClassName]
 * @property {string} [mapClassName]
 * @property {Object} [containerSx]
 * @property {Object} [mapSx]
 */

/**
 * Dynamic Mapbox map that renders a draggable/pannable map and keeps it in sync
 * with the `center`/`zoom` props.
 *
 * @param {DynamicMapboxMapProps} props
 */
const DynamicMapboxMapComponent = ({
  center = null,
  zoom = config.maps.fuzzy.enabled ? config.maps.fuzzy.defaultZoomLevel : 11,
  mapsConfig = config.maps,
  mapPicker,
  handleChangeLocation,
  containerClassName,
  mapClassName,
  containerSx = null,
  mapSx = null,
}) => {
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const store = useStore();
  const intl = useIntl();
  const centerMarkerRef = useRef(null);
  const fuzzyLayerIdRef = useRef(generateFuzzyLayerId());

  // Keep latest prop values accessible inside map event handlers (which are
  // registered once on mount) without re-running the mount effect.
  const propsRef = useRef();
  propsRef.current = { center, mapsConfig, handleChangeLocation };

  const createCenterMapMarker = () => {
    const onDragEnd = (lngLat) => {
      mapRef.current.setCenter(lngLat);
    };
    centerMarkerRef.current = mapMarker({
      ...propsRef.current.mapsConfig.customMarker,
      enabled: true,
      onDragEnd,
    });
  };

  const updateFuzzyCirclelayer = () => {
    if (!mapRef.current) {
      // map already removed
      return;
    }
    const { center, mapsConfig } = propsRef.current;
    const { lat, lng } = center;
    const position = [lng, lat];

    mapRef.current.removeLayer(fuzzyLayerIdRef.current);

    // We have to use a different layer id to avoid Mapbox errors
    fuzzyLayerIdRef.current = generateFuzzyLayerId();
    mapRef.current.addLayer(circleLayer(center, mapsConfig, fuzzyLayerIdRef.current));

    mapRef.current.setCenter(position);
  };

  const handleMapPan = () => {
    const center = mapRef.current.getCenter();
    if (centerMarkerRef.current) {
      centerMarkerRef.current.setLngLat(center);
    } else {
      createCenterMapMarker();
      centerMarkerRef.current?.setLngLat(center);
    }
    const latLng = mapboxLngLatToSDKLatLng(center);
    propsRef.current.handleChangeLocation(latLng, true);
  };

  // componentDidMount / componentWillUnmount
  useEffect(() => {
    const { center, mapsConfig } = propsRef.current;
    const position = [center.lng, center.lat];

    const map = new window.mapboxgl.Map({
      container: mapContainerRef.current,
      style: 'mapbox://styles/vansiteeu/clgm0cbjg00bs01pkd51idzv9',
      center: position,
      zoom,
      scrollZoom: mapPicker,
      touchPitch: false,
    });
    mapRef.current = map;

    map.on('moveend', handleMapPan);

    map.dragRotate.disable();

    if (!mapPicker) {
      map.addControl(new window.mapboxgl.NavigationControl({ showCompass: false }), 'top-left');
      // Native POI category control, stacked under the zoom control (top-left).
      map.addControl(new PoiLayerControl({ store, intl }), 'top-left');
      if (mapsConfig.fuzzy.enabled) {
        map.on('load', () => {
          map.addLayer(circleLayer(center, mapsConfig, fuzzyLayerIdRef.current));
        });
      } else {
        centerMarkerRef.current = mapMarker(mapsConfig.customMarker);
        centerMarkerRef.current.setLngLat(position).addTo(map);
      }
    } else {
      map.addControl(new window.mapboxgl.NavigationControl({ showCompass: false }), 'top-left');
      map.addControl(
        new window.mapboxgl.GeolocateControl({
          positionOptions: { enableHighAccuracy: true },
          trackUserLocation: true,
          showUserHeading: false,
        })
      );

      createCenterMapMarker();
      centerMarkerRef.current.setLngLat(position).addTo(map);
    }

    return () => {
      if (mapRef.current) {
        centerMarkerRef.current = null;
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // componentDidUpdate
  const prevRef = useRef({ center, zoom });
  const isFirstRenderRef = useRef(true);
  useEffect(() => {
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false;
      prevRef.current = { center, zoom };
      return;
    }

    const prev = prevRef.current;
    prevRef.current = { center, zoom };

    if (!mapRef.current) {
      return;
    }

    const { lat, lng } = center;
    const position = [lng, lat];

    // zoom change
    if (zoom !== prev.zoom) {
      mapRef.current.setZoom(zoom);
    }

    const centerChanged = lat !== prev.center.lat || lng !== prev.center.lng;

    // center marker change
    if (centerMarkerRef.current && centerChanged) {
      centerMarkerRef.current.setLngLat(position);
      mapRef.current.setCenter(position);
    }

    // fuzzy circle change
    if (mapsConfig.fuzzy.enabled && centerChanged) {
      if (mapRef.current.loaded()) {
        updateFuzzyCirclelayer();
      } else {
        mapRef.current.on('load', updateFuzzyCirclelayer);
      }
    }

    // NOTE: mapsConfig changes are not handled
     
  }, [center, zoom, mapsConfig]);

  return (
    <Box className={containerClassName} sx={containerSx}>
      <Box className={mapClassName} ref={mapContainerRef} sx={mapSx} />
    </Box>
  );
};

export default memo(DynamicMapboxMapComponent);
